/*
  PDP gallery follows the colour picker (Hit Hero, 2026-09-19).

  The PDP has no native Shopify variant picker. Colour is chosen in the buy card's disc rail,
  which is a pack builder, not a variant selector, so nothing on the page ever changed
  product.selected_or_first_available_variant. The gallery is scoped to the selected variant's
  colour (see snippets/product-media-gallery-content.liquid), which without this file would
  mean it is scoped to Black forever.

  So: tapping a colour disc re-renders the media gallery through the Section Rendering API with
  that variant selected, and swaps the node. Server-rendered, so the colour filter is the same
  one Liquid applies on a cold load. No duplicate filtering logic on the client.

  The rail lets a shopper pick two colours. The gallery cannot show two, so the LAST colour
  touched wins. Decrementing is deliberately not a trigger: removing a unit is not a request
  to look at it.

  CONCURRENCY, rewritten 2026-09-19 after review found two faults in the first version:

  1. `shown` is committed only AFTER a successful swap. The first version set it before the
     fetch, so a request that 404ed or timed out left that colour marked as current and every
     later tap on it returned early. The colour was permanently unreachable until reload.
  2. Every request carries a monotonic token and the response is discarded unless it is still
     the newest. abort() alone was not enough: a response that has already resolved `fetch`
     can finish parsing after a newer tap and overwrite the newer gallery with older markup.

  The listener binds once per document even if the snippet renders more than once on a page,
  which it can (featured-product blocks), otherwise a click would fire N parallel fetches.

  REWRITE 2026-09-23, after the owner walked the PDP on an iPhone: "when you go to select
  multiple colours on the PDP image, it's janky and shutters, and when you select one colour it
  takes you to one of the preview detail images and not the actual product image itself."

  Two faults, both measured in WebKit at 390x648:
  1. Two writers. hh-buy-card.js swapPdpGallery() painted the new render into the OLD gallery at
     about t+80ms, then this file replaceWith()ed the whole media-gallery node at about t+600ms.
     Every tap repainted twice and rebuilt the slideshow. That is the shutter.
  2. A replaced slideshow re-runs its connect logic and can open on whatever slide it chooses,
     which is how a colour tap landed on a detail shot instead of the product render.

  Now: this file is the ONLY writer (swapPdpGallery returns early when it sees the flag below).
  On tap it snaps the slideshow to slide 0 without animation and crossfades that one image to
  the colour's render, which the disc already carries in data-hero. When the Section Rendering
  API response arrives it PATCHES the other colour-aware slides and thumbnails in place, img by
  img, and never touches an image that already shows the right file. The node is only replaced
  as a fallback when the two galleries do not have the same shape.
*/
(() => {
  if (window.__hhPdpGalleryVariantBound) return;
  window.__hhPdpGalleryVariantBound = true;

  const DISC = '.hh-bc__disc[data-variant-id]';
  const FADE_MS = 200;
  let shown = null;
  let token = 0;
  let fadeToken = 0;
  let controller = null;

  /* The fading layer is positioned over the slide-0 image. Injected once rather than shipped in
     the gallery snippet, so it survives any fallback node replacement. */
  const style = document.createElement('style');
  style.textContent =
    '.hh-gallery-fade-box{position:relative}' +
    '.hh-gallery-fade{position:absolute;top:0;left:0;width:100%;height:100%;opacity:0;pointer-events:none;' +
    'transition:opacity ' + FADE_MS + 'ms ease}' +
    '@media (prefers-reduced-motion: reduce){.hh-gallery-fade{transition:none}}';
  document.head.appendChild(style);

  /* Compare by file, not by URL: the disc carries a width=1200 URL and the gallery a srcset for
     the same file, and re-pointing an image at its own file is a reload the shopper sees. */
  const fileOf = (url) => String(url || '').split('?')[0].split('/').pop();

  /* Scroll the track so slide `index` is the one actually on screen. Horizon's select() cannot do
     this when it believes that slide is already current (it returns early), and see the arrival
     guard below for how the two fall out of step. Instant, never smooth. */
  function alignTo(gallery, index) {
    const track = gallery.querySelector('slideshow-slides');
    const slide = gallery.querySelectorAll('slideshow-slide')[index];
    if (!track || !slide) return;
    const delta = slide.getBoundingClientRect().left - track.getBoundingClientRect().left;
    if (Math.abs(delta) < 2) return;
    const previous = track.style.scrollBehavior;
    track.style.scrollBehavior = 'auto';
    track.scrollLeft += delta;
    track.style.scrollBehavior = previous;
  }

  function firstSlide(gallery) {
    const slideshow = gallery.querySelector('slideshow-component');
    if (slideshow && typeof slideshow.select === 'function' && slideshow.current !== 0) {
      try { slideshow.select(0, undefined, { animate: false }); } catch (error) { /* theme version */ }
    }
    alignTo(gallery, 0);
  }

  /* ARRIVAL GUARD, 2026-09-23. Measured on live AND staging in iPhone WebKit: arriving at the PDP
     from the homepage, the gallery track opened on a random slide (6, 7, 8, 9 or 11 across runs)
     while Horizon's slideshow still reported slide 0. No script moved it; WebKit re-snapped the
     track during first layout. The shopper lands on a detail shot, and because Horizon thinks it is
     on slide 0, a colour tap then repainted slide 0 off screen. That is the owner's "selecting one
     colour takes you to one of the preview detail images". For the first seconds, until the shopper
     touches the gallery, any scroll that disagrees with Horizon's current slide is put back. */
  function guardArrival() {
    const gallery = document.querySelector('media-gallery');
    if (!gallery) return;
    const track = gallery.querySelector('slideshow-slides');
    const slideshow = gallery.querySelector('slideshow-component');
    if (!track || !slideshow) return;
    let settled = false;
    let timer = 0;
    const settle = () => { settled = true; };
    ['pointerdown', 'touchstart', 'wheel', 'keydown'].forEach((name) => gallery.addEventListener(name, settle, { passive: true, once: true }));
    setTimeout(settle, 5000);
    const check = () => {
      if (settled || !track.isConnected) return;
      alignTo(gallery, slideshow.current || 0);
    };
    track.addEventListener('scroll', () => { clearTimeout(timer); timer = setTimeout(check, 80); }, { passive: true });
    window.addEventListener('load', check, { once: true });
    check();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', guardArrival, { once: true });
  else guardArrival();

  function crossfade(gallery, url, alt) {
    const img = gallery.querySelector('slideshow-slide img');
    if (!img || !url || fileOf(img.currentSrc || img.src) === fileOf(url)) return;
    const mine = ++fadeToken;
    const preload = new Image();
    preload.onload = preload.onerror = () => {
      if (mine !== fadeToken || !img.isConnected) return;
      const box = img.parentNode;
      const layer = img.cloneNode(false);
      layer.removeAttribute('srcset');
      layer.removeAttribute('sizes');
      layer.removeAttribute('loading');
      layer.removeAttribute('id');
      layer.src = url;
      layer.alt = '';
      layer.setAttribute('aria-hidden', 'true');
      layer.classList.add('hh-gallery-fade');
      box.classList.add('hh-gallery-fade-box');
      box.appendChild(layer);
      requestAnimationFrame(() => requestAnimationFrame(() => { layer.style.opacity = '1'; }));
      setTimeout(() => {
        /* A newer tap owns the image now; just clear this layer from under it. */
        if (mine !== fadeToken || !img.isConnected) { layer.remove(); return; }
        img.removeAttribute('srcset');
        img.removeAttribute('sizes');
        img.src = url;
        img.alt = alt;
        const clear = () => requestAnimationFrame(() => layer.remove());
        if (img.complete) clear(); else img.addEventListener('load', clear, { once: true });
      }, FADE_MS + 20);
    };
    preload.src = url;
  }

  function patch(live, next) {
    /* The fade layer is a temporary extra img; counting it made a fast response fall back to a
       full node replacement, which is the very rebuild this file exists to avoid. */
    const liveImgs = [...live.querySelectorAll('img')].filter((img) => !img.classList.contains('hh-gallery-fade'));
    const nextImgs = next.querySelectorAll('img');
    const liveMedia = live.querySelectorAll('[data-media-id]');
    const nextMedia = next.querySelectorAll('[data-media-id]');
    if (liveImgs.length !== nextImgs.length || liveMedia.length !== nextMedia.length) return false;
    liveImgs.forEach((img, index) => {
      const incoming = nextImgs[index];
      if (fileOf(img.getAttribute('src')) === fileOf(incoming.getAttribute('src'))) return;
      ['srcset', 'sizes', 'src', 'alt'].forEach((name) => {
        const value = incoming.getAttribute(name);
        if (value === null) img.removeAttribute(name); else img.setAttribute(name, value);
      });
    });
    liveMedia.forEach((node, index) => {
      node.setAttribute('data-media-id', nextMedia[index].getAttribute('data-media-id'));
    });
    return true;
  }

  async function showVariant(disc) {
    const variantId = disc.dataset.variantId;
    if (!variantId || variantId === shown) return;

    const gallery = document.querySelector('media-gallery');
    if (!gallery) return;
    const section = gallery.closest('.shopify-section');
    const sectionId = section && section.id.replace(/^shopify-section-/, '');
    if (!sectionId) return;

    /* The visible change happens now, from data the disc already carries. */
    firstSlide(gallery);
    crossfade(gallery, disc.dataset.hero || disc.dataset.image, (disc.dataset.name || '') + ' Hit Hero baseball thumb guard');

    token += 1;
    const mine = token;
    if (controller) controller.abort();
    controller = new AbortController();

    try {
      const url = `${location.pathname}?variant=${encodeURIComponent(variantId)}&section_id=${encodeURIComponent(sectionId)}`;
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok || mine !== token) return;
      const markup = await response.text();
      if (mine !== token) return;
      const next = new DOMParser().parseFromString(markup, 'text/html').querySelector('media-gallery');
      /* Re-query: a fallback replacement may have swapped the node captured above. */
      const live = document.querySelector('media-gallery');
      if (!next || !live) return;
      if (!patch(live, next)) {
        live.replaceWith(next);
        requestAnimationFrame(() => firstSlide(next));
      }
      shown = variantId;
    } catch (error) {
      if (error && error.name !== 'AbortError') {
        console.warn('[hh-pdp-gallery-variant] gallery update failed:', error);
      }
    }
  }

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!target || typeof target.closest !== 'function') return;
      const disc = target.closest(DISC);
      if (!disc) return;
      if (target.closest('.hh-bc__disc-stepper button:first-child')) return;
      showVariant(disc);
    },
    { passive: true }
  );
})();
